import React, { useState } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Grid, 
  Card, 
  CardContent,
  CircularProgress,
  Alert,
  Chip,
      

  LinearProgress,
  alpha
} from '@mui/material';
import { 
  CalendarMonth as CalendarMonthIcon,
  Schedule as ScheduleIcon,
  Business as BusinessIcon,
  Build as BuildIcon,
  People as PeopleIcon,
  AccessTime as AccessTimeIcon,
  Phone as PhoneIcon
} from '@mui/icons-material';
import { useAuth } from '../../../context/AuthContext';
import {
  usePermissionControlledSchedulesQuery,
  usePermissionControlledMemberAppointmentsQuery,
  usePermissionControlledMemberSalonsQuery,
  usePermissionControlledUserServicesQuery,
  usePermissionControlledUserTeamsQuery
} from '../../../hooks';
// import { useAppointmentsEmployeeTodayRetrieveQuery } from '../../../services/GLOWClient'; // TODO: Endpoint not generated
import AppointmentCalendarGrid from '../shared/AppointmentCalendarGrid';
import MarkAttendance from '../appointment/MarkAttendance';

import { theme } from '../../../theme';
import moment from 'moment';
import 'moment/locale/fr';

moment.locale('fr');

const EmployeeDashboard = () => {
  const { user, teamMemberId, isEmployee } = useAuth();
  const [startDate] = useState(moment().format('YYYY-MM-DD'));
  const [endDate] = useState(moment().format('YYYY-MM-DD'));
  const [refreshKey, setRefreshKey] = useState(0);

  const dateStr = new Date().toLocaleDateString('fr-FR', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  // These hooks will automatically use the employee's ID when role=employee
  const { 
    data: scheduleData, 
    isLoading: scheduleLoading, 
    error: scheduleError 
  } = usePermissionControlledSchedulesQuery(
    teamMemberId ? { teamMemberId } : undefined,
    { skip: !teamMemberId }
  );

  // Récupérer les rendez-vous de l'employé
  const { 
    data: appointmentsData, 
    isLoading: appointmentsLoading, 
    error: appointmentsError,
    refetch: refetchAppointments
  } = usePermissionControlledMemberAppointmentsQuery(
    teamMemberId ? { 
      teamMemberId,
      startDate,
      endDate
    } : undefined,
    { 
      skip: !teamMemberId,
      refetchOnMountOrArgChange: true
    }
  );

  const todayAppointments = React.useMemo(() => {
    if (!appointmentsData) return [];
    return Array.isArray(appointmentsData) ? appointmentsData : [];
  }, [appointmentsData, refreshKey]);

  const handleRefresh = () => {
    setRefreshKey(prev => prev + 1);
    refetchAppointments();
  };
  const salonsFromApi = React.useMemo(() => [], []);

  const { 
    data: servicesData, 
    isLoading: servicesLoading, 
    error: servicesError 
  } = usePermissionControlledUserServicesQuery(
    user ? { userId: user.id } : undefined,
    { skip: !user }
  );

  const { 
    data: teamsData, 
    isLoading: teamsLoading, 
    error: teamsError 
  } = usePermissionControlledUserTeamsQuery(
    user ? { userId: user.id } : undefined,
    { skip: !user }
  );

  // Build unique salons list from user_teams response
  const salonsFromTeams = React.useMemo(() => {
    if (!teamsData || !Array.isArray(teamsData)) return [];
    const seen = new Set();
    const salons = [];
    for (const team of teamsData) {
      const salonId = team.salon;
      const salonName = team.salon_name;
      if (salonId && !seen.has(salonId)) {
        seen.add(salonId);
        salons.push({ id: salonId, name: salonName });
      }
    }
    return salons;
  }, [teamsData]);

  // Get status color helper
  const getStatusColor = (status) => {
    switch(status) {
      case 'confirmed':
        return theme.colors.success[500];
      case 'pending':
        return theme.colors.warning[500];
      case 'cancelled':
        return theme.colors.error[500];
      case 'completed':
        return theme.colors.info[500];
      default:
        return theme.colors.primary[500];
    }
  };

  const getStatusLabel = (status) => {
    switch(status) {
      case 'confirmed':
        return 'Confirmé';
      case 'pending':
        return 'En attente';
      case 'cancelled':
        return 'Annulé';
      case 'completed':
        return 'Terminé';
      default:
        return status;
    }
  };

  // If not an employee, show a message
  if (!isEmployee()) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="info">
          Ce tableau de bord est uniquement disponible pour les employés.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography 
          variant="h4" 
          sx={{ 
            fontWeight: 800, 
            color: theme.colors.text.primary,
            mb: 1,
            background: `linear-gradient(135deg, ${theme.colors.primary[600]} 0%, ${theme.colors.primary[800]} 100%)`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}
        >
          Mon Tableau de Bord
        </Typography>
        <Typography variant="body1" sx={{ color: theme.colors.text.secondary, textTransform: 'capitalize' }}>
          {dateStr}
        </Typography>
      </Box>
      
      <Grid container spacing={3}>
        {/* Mes Rendez-vous à venir - Format Agenda */}
        <Grid item xs={12} md={8}>
          <Card 
            elevation={0}
            sx={{ 
              height: '100%',
              border: `1px solid ${theme.colors.success[100]}`,
              borderRadius: 3,
              background: `linear-gradient(135deg, ${alpha(theme.colors.success[50], 0.3)} 0%, ${alpha(theme.colors.success[100], 0.2)} 100%)`,
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: `0 12px 24px ${alpha(theme.colors.success[500], 0.15)}`
              }
            }}
          >
            <Box sx={{ 
              p: { xs: 1.5, sm: 2.5 },
              background: `linear-gradient(135deg, ${theme.colors.success[500]} 0%, ${theme.colors.success[700]} 100%)`,
              borderBottom: `3px solid ${theme.colors.success[800]}`,
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 1, sm: 2 }
            }}>
              <Box sx={{ 
                width: { xs: 40, sm: 48 },
                height: { xs: 40, sm: 48 },
                borderRadius: 2.5,
                background: `linear-gradient(135deg, ${alpha('#fff', 0.2)} 0%, ${alpha('#fff', 0.1)} 100%)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: `0 4px 12px ${alpha('#000', 0.2)}`,
                flexShrink: 0
              }}>
                <CalendarMonthIcon sx={{ fontSize: { xs: 20, sm: 24 }, color: '#fff' }} />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#fff', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                Mes Rendez-vous à venir
              </Typography>
            </Box>
            <CardContent sx={{ p: 3 }}>
              {appointmentsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress size={40} sx={{ color: theme.colors.success[500] }} />
                </Box>
              ) : appointmentsError ? (
                <Alert severity="error" sx={{ borderRadius: 2 }}>
                  Erreur lors du chargement des rendez-vous
                </Alert>
              ) : todayAppointments.length === 0 ? (
                <Typography sx={{ color: theme.colors.text.secondary, textAlign: 'center', py: 3 }}>
                  Aucun rendez-vous aujourd'hui
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {todayAppointments.map((appointment) => {
                    const isPast = moment(appointment.start_time).isBefore(moment());
                    
                    return (
                      <Box 
                        key={appointment.appointment_id} 
                        sx={{ 
                          p: 2.5,
                          borderRadius: 2,
                          background: '#fff',
                          border: `1px solid ${theme.colors.success[100]}`,
                          boxShadow: `0 2px 8px ${alpha(theme.colors.success[500], 0.1)}`,
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            borderColor: theme.colors.success[300],
                            boxShadow: `0 4px 12px ${alpha(theme.colors.success[500], 0.2)}`
                          }
                        }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                          <Box sx={{ flex: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                              <AccessTimeIcon sx={{ fontSize: 18, color: theme.colors.text.secondary }} />
                              <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: theme.colors.text.primary }}>
                                {moment(appointment.start_time).format('HH:mm')} - {moment(appointment.end_time).format('HH:mm')}
                              </Typography>
                            </Box>
                            <Typography sx={{ fontSize: '0.875rem', color: theme.colors.text.secondary, mb: 1 }}>
                              {appointment.service_name || 'Service non spécifié'}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                              <PeopleIcon sx={{ fontSize: 16, color: theme.colors.text.secondary }} />
                              <Typography sx={{ fontSize: '0.875rem', color: theme.colors.text.secondary }}>
                                {appointment.client_name || 'Client non spécifié'}
                              </Typography>
                            </Box>
                            {appointment.client_phone && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <PhoneIcon sx={{ fontSize: 16, color: theme.colors.text.secondary }} />
                                <Typography sx={{ fontSize: '0.875rem', color: theme.colors.text.secondary }}>
                                  {appointment.client_phone}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip 
                              label={getStatusLabel(appointment.status)}
                              size="small"
                              sx={{ 
                                backgroundColor: alpha(getStatusColor(appointment.status), 0.1),
                                color: getStatusColor(appointment.status),
                                fontWeight: 600
                              }}
                            />
                            {isPast && (
                              <MarkAttendance 
                                appointment={appointment} 
                                onUpdate={handleRefresh}
                              />
                            )}
                          </Box>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Colonne de droite - Mes Salons, Services, Équipes */}
        <Grid item xs={12} md={4}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr' }, gap: 3 }}>
            {/* Mes Salons */}
            <Card 
              elevation={0}
              sx={{ 
                border: `1px solid ${theme.colors.secondary[100]}`,
                borderRadius: 3,
                background: `linear-gradient(135deg, ${alpha(theme.colors.secondary[50], 0.3)} 0%, ${alpha(theme.colors.secondary[100], 0.2)} 100%)`,
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: `0 12px 24px ${alpha(theme.colors.secondary[500], 0.15)}`
                }
              }}
            >
            <Box sx={{ 
              p: { xs: 1.5, sm: 2.5 },
              background: `linear-gradient(135deg, ${theme.colors.secondary[500]} 0%, ${theme.colors.secondary[700]} 100%)`,
              borderBottom: `3px solid ${theme.colors.secondary[800]}`,
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 1, sm: 2 }
            }}>
              <Box sx={{ 
                width: { xs: 40, sm: 48 },
                height: { xs: 40, sm: 48 },
                borderRadius: 2.5,
                background: `linear-gradient(135deg, ${alpha('#fff', 0.2)} 0%, ${alpha('#fff', 0.1)} 100%)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: `0 4px 12px ${alpha('#000', 0.2)}`,
                flexShrink: 0
              }}>
                <BusinessIcon sx={{ fontSize: { xs: 20, sm: 24 }, color: '#fff' }} />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#fff', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                Mes Salons
              </Typography>
            </Box>
            <CardContent sx={{ p: 3 }}>
              {teamsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress size={40} sx={{ color: theme.colors.secondary[500] }} />
                </Box>
              ) : teamsError ? (
                <Alert severity="error" sx={{ borderRadius: 2 }}>
                  Erreur lors du chargement des salons
                </Alert>
              ) : salonsFromTeams.length === 0 ? (
                <Typography sx={{ color: theme.colors.text.secondary, textAlign: 'center', py: 3 }}>
                  Aucun salon assigné
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {salonsFromTeams.map((salon) => (
                    <Box key={salon.id} sx={{ 
                      p: 2.5,
                      borderRadius: 2,
                      background: '#fff',
                      border: `1px solid ${theme.colors.secondary[100]}`,
                      boxShadow: `0 2px 8px ${alpha(theme.colors.secondary[500], 0.1)}`,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        borderColor: theme.colors.secondary[300],
                        boxShadow: `0 4px 12px ${alpha(theme.colors.secondary[500], 0.2)}`
                      }
                    }}>
                      <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: theme.colors.text.primary, mb: 0.5 }}>
                        {salon.name}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>

            {/* Mes Services */}
            <Card 
              elevation={0}
              sx={{ 
                border: `1px solid ${theme.colors.info[100]}`,
                borderRadius: 3,
                background: `linear-gradient(135deg, ${alpha(theme.colors.info[50], 0.3)} 0%, ${alpha(theme.colors.info[100], 0.2)} 100%)`,
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: `0 12px 24px ${alpha(theme.colors.info[500], 0.15)}`
                }
              }}
            >
            <Box sx={{ 
              p: { xs: 1.5, sm: 2.5 },
              background: `linear-gradient(135deg, ${theme.colors.info[500]} 0%, ${theme.colors.info[700]} 100%)`,
              borderBottom: `3px solid ${theme.colors.info[800]}`,
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 1, sm: 2 }
            }}>
              <Box sx={{ 
                width: { xs: 40, sm: 48 },
                height: { xs: 40, sm: 48 },
                borderRadius: 2.5,
                background: `linear-gradient(135deg, ${alpha('#fff', 0.2)} 0%, ${alpha('#fff', 0.1)} 100%)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: `0 4px 12px ${alpha('#000', 0.2)}`,
                flexShrink: 0
              }}>
                <BuildIcon sx={{ fontSize: { xs: 20, sm: 24 }, color: '#fff' }} />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#fff', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                Mes Services
              </Typography>
            </Box>
            <CardContent sx={{ p: 3 }}>
              {servicesLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress size={40} sx={{ color: theme.colors.info[500] }} />
                </Box>
              ) : servicesError ? (
                <Alert severity="error" sx={{ borderRadius: 2 }}>
                  Erreur lors du chargement des services: {servicesError.message || 'Erreur inconnue'}
                </Alert>
              ) : !servicesData || servicesData.length === 0 ? (
                <Typography sx={{ color: theme.colors.text.secondary, textAlign: 'center', py: 3 }}>
                  Aucun service assigné
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {servicesData.map((service) => (
                    <Box key={service.id} sx={{ 
                      p: 2.5,
                      borderRadius: 2,
                      background: '#fff',
                      border: `1px solid ${theme.colors.info[100]}`,
                      boxShadow: `0 2px 8px ${alpha(theme.colors.info[500], 0.1)}`,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        borderColor: theme.colors.info[300],
                        boxShadow: `0 4px 12px ${alpha(theme.colors.info[500], 0.2)}`
                      }
                    }}>
                      <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: theme.colors.text.primary, mb: 1 }}>
                        {service.name}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Chip 
                          label={`${service.duration} min`}
                          size="small"
                          sx={{ 
                            backgroundColor: theme.colors.info[50],
                            color: theme.colors.info[700],
                            fontWeight: 600
                          }}
                        />
                        <Chip 
                          label={`${service.price} DA`}
                          size="small"
                          sx={{ 
                            backgroundColor: theme.colors.success[50],
                            color: theme.colors.success[700],
                            fontWeight: 600
                          }}
                        />
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>

            {/* Mes Équipes */}
            <Card 
              elevation={0}
              sx={{ 
                border: `1px solid ${theme.colors.warning[100]}`,
                borderRadius: 3,
                background: `linear-gradient(135deg, ${alpha(theme.colors.warning[50], 0.3)} 0%, ${alpha(theme.colors.warning[100], 0.2)} 100%)`,
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: `0 12px 24px ${alpha(theme.colors.warning[500], 0.15)}`
                }
              }}
            >
            <Box sx={{ 
              p: { xs: 1.5, sm: 2.5 },
              background: `linear-gradient(135deg, ${theme.colors.warning[500]} 0%, ${theme.colors.warning[700]} 100%)`,
              borderBottom: `3px solid ${theme.colors.warning[800]}`,
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 1, sm: 2 }
            }}>
              <Box sx={{ 
                width: { xs: 40, sm: 48 },
                height: { xs: 40, sm: 48 },
                borderRadius: 2.5,
                background: `linear-gradient(135deg, ${alpha('#fff', 0.2)} 0%, ${alpha('#fff', 0.1)} 100%)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: `0 4px 12px ${alpha('#000', 0.2)}`,
                flexShrink: 0
              }}>
                <PeopleIcon sx={{ fontSize: { xs: 20, sm: 24 }, color: '#fff' }} />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#fff', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                Mes Équipes
              </Typography>
            </Box>
            <CardContent sx={{ p: 3 }}>
              {teamsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress size={40} sx={{ color: theme.colors.warning[500] }} />
                </Box>
              ) : teamsError ? (
                <Alert severity="error" sx={{ borderRadius: 2 }}>
                  Erreur lors du chargement des équipes: {teamsError.message || 'Erreur inconnue'}
                </Alert>
              ) : !teamsData || teamsData.length === 0 ? (
                <Typography sx={{ color: theme.colors.text.secondary, textAlign: 'center', py: 3 }}>
                  Aucune équipe assignée
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {teamsData.map((team) => (
                    <Box key={team.id} sx={{ 
                      p: 2.5,
                      borderRadius: 2,
                      background: '#fff',
                      border: `1px solid ${theme.colors.warning[100]}`,
                      boxShadow: `0 2px 8px ${alpha(theme.colors.warning[500], 0.1)}`,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        borderColor: theme.colors.warning[300],
                        boxShadow: `0 4px 12px ${alpha(theme.colors.warning[500], 0.2)}`
                      }
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ 
                          width: 40,
                          height: 40,
                          borderRadius: 2,
                          background: `linear-gradient(135deg, ${theme.colors.warning[500]} 0%, ${theme.colors.warning[700]} 100%)`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0
                        }}>
                          <PeopleIcon sx={{ fontSize: 20, color: '#fff' }} />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: theme.colors.text.primary, mb: 0.5 }}>
                            {team.name}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <BusinessIcon sx={{ fontSize: 16, color: theme.colors.text.secondary }} />
                            <Typography sx={{ fontSize: '0.875rem', color: theme.colors.text.secondary }}>
                              {team.salon_name || 'Non assigné'}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
          </Box>
        </Grid>
      </Grid>
    </Container>
  );
};

export default EmployeeDashboard;
